import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./PortfolioStudio.css";

type SourceType =
  | "LIFE_STAMP"
  | "EXPERIENCE"
  | "ACHIEVEMENT"
  | "COMPETENCY"
  | "EVIDENCE"
  | "ASSESSMENT_RESULT"
  | "GOAL";

type DocumentType =
  | "SELF_INTRODUCTION"
  | "ACTIVITY_RECORD"
  | "CAREER_PORTFOLIO"
  | "PROJECT_PORTFOLIO"
  | "GROWTH_REPORT";

type PortfolioPurpose =
  | "SCHOOL_RECORD"
  | "MAJOR_EXPLORATION"
  | "PROJECT_SHOWCASE"
  | "INTERVIEW"
  | "PERSONAL_GROWTH";

type PortfolioMeta = {
  purpose: PortfolioPurpose;
  target: string;
  headline: string;
};

type ReadinessCheck = {
  label: string;
  done: boolean;
  weight: number;
  help: string;
};

type AiWritingAction =
  | "FULL_DRAFT"
  | "POLISH"
  | "MORE_SPECIFIC"
  | "SHORTEN"
  | "STUDENT_TONE"
  | "INTERVIEW_TONE"
  | "ADMISSION_TONE";

type AiWritingResponse = {
  model?: string;
  sections?: Array<{ key: string; title: string; body: string }>;
  warnings?: string[];
  code?: string;
  message?: string;
};

type PortfolioSource = {
  id: string;
  sourceType: SourceType;
  sourceId: string;
  title: string;
  description?: string | null;
  occurredAt?: string | null;
  trustStatus: string;
  trustLabel: string;
  details: Record<string, unknown>;
  relatedSourceIds: string[];
};

type LearnerProfile = {
  learnerId: string;
  displayName: string;
  schoolName?: string | null;
  gradeLevel?: string | null;
  classLabel?: string | null;
};

type Section = {
  key: string;
  title: string;
  body: string;
  visible: boolean;
  sourceIds: string[];
};

type VersionItem = { versionNo: number; createdAt: string };
type ShareLink = {
  shareId: string;
  status: string;
  shareUrl: string;
  expiresAt: string;
  includeLifeStamps: boolean;
  includeExperiences: boolean;
};
type LibraryItem = {
  documentId: string;
  documentType: DocumentType;
  title: string;
  status: "DRAFT" | "ARCHIVED";
  currentVersion: number;
  createdAt: string;
  updatedAt: string;
  sourceCount: number;
  shareLinks: ShareLink[];
};
type DocumentPayload = {
  documentId: string;
  documentType: DocumentType;
  title: string;
  status: "DRAFT" | "ARCHIVED";
  currentVersion: number;
  versionNo: number;
  content: { sections?: Section[]; portfolioMeta?: PortfolioMeta; [key: string]: unknown };
  sources: PortfolioSource[];
  versions: VersionItem[];
  shareLinks: ShareLink[];
};
type Prefill = { sourceType: SourceType; sourceId: string; documentType?: DocumentType };

type CatalogPayload = { learner: LearnerProfile; sources: PortfolioSource[] };

type ComposerInput = {
  type: DocumentType;
  profile: LearnerProfile;
  sources: PortfolioSource[];
};

export interface DocumentComposer {
  compose(input: ComposerInput): Section[];
}

export interface DocumentEnhancer {
  enhance(sections: Section[]): Promise<Section[]>;
}

const DOCUMENT_TYPES: Array<{
  key: DocumentType;
  title: string;
  description: string;
  icon: string;
}> = [
  {
    key: "SELF_INTRODUCTION",
    title: "자기소개서",
    description: "Life Stamp와 경험을 바탕으로 나의 강점과 성장 과정을 정리합니다.",
    icon: "01"
  },
  {
    key: "ACTIVITY_RECORD",
    title: "활동기술서",
    description: "선택한 활동의 역할·과정·결과·성취를 학생의 말로 정리합니다.",
    icon: "02"
  },
  {
    key: "CAREER_PORTFOLIO",
    title: "진로성장 포트폴리오",
    description: "여러 경험과 Life Stamp를 묶어 전체 성장 과정을 표현합니다.",
    icon: "03"
  },
  {
    key: "PROJECT_PORTFOLIO",
    title: "프로젝트 포트폴리오",
    description: "하나의 프로젝트 또는 관련 경험 묶음을 깊이 있게 정리합니다.",
    icon: "04"
  },
  {
    key: "GROWTH_REPORT",
    title: "성장 리포트",
    description: "특정 기간의 경험·검사·역량·성취 기록을 살펴봅니다.",
    icon: "05"
  }
];

const SOURCE_TYPES: Array<{ key: SourceType; label: string; help: string }> = [
  { key: "LIFE_STAMP", label: "Life Stamp", help: "발급기관과 현재 유효 상태를 함께 표시합니다." },
  { key: "EXPERIENCE", label: "경험", help: "학교 공식 기록과 학생 자기기록을 출처별로 구분합니다." },
  { key: "ACHIEVEMENT", label: "Achievement", help: "교사가 확인한 성취 기록입니다." },
  { key: "COMPETENCY", label: "Competency", help: "평가 기록에 연결된 역량 결과입니다." },
  { key: "EVIDENCE", label: "증빙", help: "활동에 연결된 증빙이며 학생 기록과 교사 확인을 구분합니다." },
  { key: "ASSESSMENT_RESULT", label: "Assessment Result", help: "자기이해 자료이며 진로나 직업을 확정하지 않습니다." },
  { key: "GOAL", label: "Goal", help: "여정 기록으로 표시되며 공식 인증으로 표현하지 않습니다." }
];

const PORTFOLIO_PURPOSES: Array<{ key: PortfolioPurpose; label: string; help: string }> = [
  { key: "SCHOOL_RECORD", label: "학교 활동 정리", help: "수업·동아리·진로활동을 사실 중심으로 정리합니다." },
  { key: "MAJOR_EXPLORATION", label: "대학·학과 탐색", help: "관심 분야와 경험, 역량, 다음 탐색 행동을 연결합니다." },
  { key: "PROJECT_SHOWCASE", label: "프로젝트 소개", help: "문제·역할·행동·결과·증거를 한 흐름으로 보여 줍니다." },
  { key: "INTERVIEW", label: "면접 준비", help: "질문에 답할 수 있도록 경험과 근거를 짧고 명확하게 정리합니다." },
  { key: "PERSONAL_GROWTH", label: "성장 기록", help: "경험 전후의 변화와 배움, 다음 행동을 남깁니다." }
];

const SECTION_TEMPLATES: Array<{ key: string; label: string; title: string; body: string }> = [
  {
    key: "star",
    label: "STAR",
    title: "STAR 경험 정리",
    body: "상황(Situation)\n- 어떤 상황이었나요?\n\n과제(Task)\n- 내가 맡은 과제나 목표는 무엇이었나요?\n\n행동(Action)\n- 내가 실제로 한 행동을 구체적으로 적어 주세요.\n\n결과(Result)\n- 결과와 확인 가능한 근거를 적어 주세요."
  },
  {
    key: "problem",
    label: "문제해결",
    title: "문제 해결 과정",
    body: "문제\n- 해결해야 했던 문제는 무엇이었나요?\n\n시도\n- 어떤 방법을 시도했나요?\n\n조정\n- 잘되지 않았던 점과 바꾼 점은 무엇인가요?\n\n결과\n- 무엇이 달라졌나요?"
  },
  {
    key: "evidence",
    label: "증거",
    title: "성과와 증거",
    body: "성과\n- 내가 만든 결과를 한 문장으로 적어 주세요.\n\n증거\n- 사진, 문서, 수치, 발표, Life Stamp 등 확인 가능한 근거를 연결해 주세요.\n\n기여\n- 그 결과에서 내가 맡은 부분을 구분해 적어 주세요."
  },
  {
    key: "reflection",
    label: "성찰",
    title: "배움과 다음 행동",
    body: "배운 점\n- 이 경험을 통해 알게 된 점은 무엇인가요?\n\n변화\n- 전과 비교해 달라진 점은 무엇인가요?\n\n다음 행동\n- 다음에 실제로 해볼 한 가지 행동을 적어 주세요."
  }
];

const PREFILL_KEY = "rs-life-passport-portfolio-prefill";

function sourceKey(type: SourceType, id: string) {
  return type + ":" + id;
}

function formatDate(value?: string | null) {
  if (!value) return "날짜 정보 없음";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "날짜 정보 없음";
  return date.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function shortDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function typeLabel(type: DocumentType) {
  return DOCUMENT_TYPES.find((item) => item.key === type)?.title || type;
}

function statusLabel(value: unknown) {
  if (value === "COMPLETED") return "완료";
  if (value === "ATTENDED") return "참석";
  if (value === "CONFIRMED") return "확인";
  if (value === "ACTIVE") return "진행 중";
  if (typeof value === "string" && value) return value;
  return "기록";
}

function field(value: unknown, fallback = ""): string {
  return value === null || value === undefined ? fallback : String(value);
}

function sourcesOfType(sources: PortfolioSource[], type: SourceType) {
  return sources.filter((source) => source.sourceType === type);
}

function sentenceForSource(source: PortfolioSource) {
  const details = source.details || {};
  if (source.sourceType === "LIFE_STAMP") {
    const issuer = field(details.issuerName, "발급기관 정보 없음");
    const achievement = field(details.achievementName, source.description || "성취 정보 없음");
    return issuer + "이(가) " + achievement + "에 대해 " + source.title + " Life Stamp를 발급했습니다.";
  }
  if (source.sourceType === "EXPERIENCE") {
    const activity = field(details.activityTitle, source.title);
    const reflection = details.studentReflection as { content?: Record<string, unknown> } | undefined;
    const learned = field(reflection?.content?.learning);
    const did = field(reflection?.content?.whatIDid);
    const isSelfReported = source.trustStatus === "SELF_REPORTED" || details.experienceType === "PERSONAL";
    const experienceSentence = isSelfReported
      ? "학생이 " + activity + " 경험을 자기기록으로 남겼습니다."
      : "학교 기록에는 " + field(details.programTitle, "프로그램 정보 없음") + "의 " + activity + " 활동이 " + statusLabel(details.status) + "로 남아 있습니다.";
    const reflectionSentence = [
      did ? "학생은 이 경험에서 " + did + "라고 기록했습니다." : "",
      learned ? "학생은 이 경험을 통해 " + learned + "라고 기록했습니다." : ""
    ].filter(Boolean).join(" ");
    return [experienceSentence, reflectionSentence].filter(Boolean).join(" ");
  }
  if (source.sourceType === "ACHIEVEMENT") {
    return "교사가 " + source.title + " 성취를 확인했습니다.";
  }
  if (source.sourceType === "COMPETENCY") {
    const score = details.score === null || details.score === undefined ? "" : " · 기록 점수 " + String(details.score);
    const statement = source.trustStatus === "TEACHER_CONFIRMED"
      ? "교사 평가 기록에서 " + source.title + " 역량이 확인되었습니다"
      : source.title + " 역량이 기관 평가 기록에 남아 있습니다";
    return statement + score + ".";
  }
  if (source.sourceType === "EVIDENCE") {
    return source.trustStatus === "TEACHER_CONFIRMED"
      ? "교사가 " + source.title + " 증빙을 확인했습니다."
      : "학생이 " + source.title + " 증빙을 활동에 추가했습니다. " + source.trustLabel + ".";
  }
  if (source.sourceType === "ASSESSMENT_RESULT") {
    const score = details.normalizedScore ?? details.rawScore;
    const suffix = score === null || score === undefined ? "" : " · 결과 " + String(score);
    return source.title + " 검사 결과가 있습니다" + suffix + ". 이 결과는 자기이해를 돕는 자료입니다.";
  }
  return source.title + " 목표가 여정에 기록되어 있습니다. " + source.trustLabel + ".";
}

function detailLines(source: PortfolioSource) {
  const d = source.details || {};
  if (source.sourceType === "EXPERIENCE") {
    const reflection = d.studentReflection as { status?: string; content?: Record<string, unknown> } | undefined;
    const content = reflection?.content || {};
    const selfReported = source.trustStatus === "SELF_REPORTED" || d.experienceType === "PERSONAL";
    return [
      "프로그램: " + field(d.programTitle, "원본에 기관·프로그램명이 없습니다."),
      "활동 유형: " + field(d.activityType, "원본에 활동 유형이 없습니다."),
      (selfReported ? "자기기록 상태: " : "학교 공식 참여 상태: ") + statusLabel(d.status),
      "일자: " + formatDate(field(d.completedAt, field(d.attendedAt, field(d.startedOn, source.occurredAt || "")))),
      "장소: " + field(d.locationName, field(d.organizationOrPlace, "원본에 장소가 없습니다.")),
      field(d.role) ? "학생이 기록한 역할: " + field(d.role) : "",
      field(content.whatIDid) ? "학생이 기록한 행동: " + field(content.whatIDid) : "",
      field(content.learning) ? "학생이 기록한 배움: " + field(content.learning) : "",
      reflection?.status ? "학생 성찰 상태: " + (reflection.status === "COMPLETED" ? "기록 완료" : "작성 중") : ""
    ].filter(Boolean);
  }
  if (source.sourceType === "EVIDENCE") {
    const files = Array.isArray(d.files) ? d.files.map((item) => field((item as Record<string, unknown>).fileName)).filter(Boolean) : [];
    return [
      "증빙 확인 상태: " + source.trustLabel,
      "활동일: " + formatDate(field(d.observedAt, source.occurredAt || "")),
      (source.trustStatus === "TEACHER_CONFIRMED" ? "확인된 증빙 내용: " : "학생이 기록한 증빙 설명: ") + field(d.narrative, source.description || "증빙 내용이 비어 있습니다."),
      files.length ? "파일: " + files.join(", ") : "첨부 파일: 원본에 표시된 항목 없음"
    ];
  }
  if (source.sourceType === "ASSESSMENT_RESULT") {
    const score = d.normalizedScore ?? d.rawScore;
    return [
      "검사: " + field(d.assessmentName, "검사명 없음"),
      "결과 영역: " + field(d.targetName, "결과 영역 없음"),
      "점수: " + (score === null || score === undefined ? "공개 점수 없음" : String(score)),
      "검사일: " + formatDate(source.occurredAt),
      "해석: " + field((d.interpretation as Record<string, unknown> | null)?.summary, "공개 해석 없음")
    ];
  }
  if (source.sourceType === "GOAL") {
    return [
      "상태: " + statusLabel(d.status),
      "목표일: " + formatDate(field(d.targetOn, "")),
      "설명: " + field(d.description, "원본에 설명이 없습니다."),
      source.trustLabel
    ];
  }
  if (source.sourceType === "LIFE_STAMP") {
    return [
      "성취: " + field(d.achievementName, source.description || "성취 정보 없음"),
      "발급기관: " + field(d.issuerName, "원본에 발급기관명이 없습니다."),
      "발급일: " + formatDate(field(d.issuedAt, source.occurredAt || "")),
      "상태: " + statusLabel(d.status),
      source.trustLabel
    ];
  }
  if (source.sourceType === "ACHIEVEMENT") {
    return [
      "성취: " + source.title,
      "설명: " + field(d.description, "원본에 설명이 없습니다."),
      "확인일: " + formatDate(field(d.confirmedAt, source.occurredAt || "")),
      source.trustLabel
    ];
  }
  return [
    "역량: " + field(d.competencyName, source.title),
    "점수: " + field(d.score, "점수 정보 없음"),
    "확인일: " + formatDate(field(d.observedAt, source.occurredAt || "")),
    source.trustLabel
  ];
}

function makeSection(
  key: string,
  title: string,
  body: string,
  sourceIds: string[] = [],
  visible = true
): Section {
  return { key, title, body, sourceIds, visible };
}

export const deterministicDocumentComposer: DocumentComposer = {
  compose({ type, profile, sources }) {
    const stamps = sourcesOfType(sources, "LIFE_STAMP");
    const experiences = sourcesOfType(sources, "EXPERIENCE");
    const achievements = sourcesOfType(sources, "ACHIEVEMENT");
    const competencies = sourcesOfType(sources, "COMPETENCY");
    const evidence = sourcesOfType(sources, "EVIDENCE");
    const assessments = sourcesOfType(sources, "ASSESSMENT_RESULT");
    const goals = sourcesOfType(sources, "GOAL");
    const ids = (items: PortfolioSource[]) => items.map((item) => item.id);
    const lines = (items: PortfolioSource[]) => items.length
      ? items.map(sentenceForSource).join("\n")
      : "선택된 기록이 없습니다. 필요한 기록을 직접 선택하거나 학생이 내용을 작성해 주세요.";
    const experienceDetails = (items: PortfolioSource[]) => items.length
      ? items.map((item) => detailLines(item).join("\n")).join("\n\n")
      : "원본 기록에 세부 내용이 없습니다. 역할과 행동은 학생이 직접 기록해 주세요.";
    const goalText = goals.length
      ? goals.map((item) => item.title + " · " + statusLabel(item.details.status) + " · " + item.trustLabel).join("\n")
      : "선택된 목표가 없습니다. 학생이 현재 목표와 다음 행동을 직접 적을 수 있습니다.";
    const displayProfile = [
      profile.displayName,
      profile.schoolName || "",
      profile.gradeLevel ? profile.gradeLevel + "학년" : "",
      profile.classLabel ? profile.classLabel + "반" : ""
    ].filter(Boolean).join(" · ") || "프로필 정보가 없습니다.";
    const periodValues = sources.map((item) => item.occurredAt).filter(Boolean).map((item) => new Date(item as string).getTime()).filter(Number.isFinite);
    const periodText = periodValues.length
      ? formatDate(new Date(Math.min(...periodValues)).toISOString()) + " ~ " + formatDate(new Date(Math.max(...periodValues)).toISOString())
      : "선택 기록에 기간 정보가 없습니다.";

    if (type === "SELF_INTRODUCTION") {
      return [
        makeSection("one-line", "나를 설명하는 한 문장", "선택한 사실 기록을 바탕으로 문서를 만들었습니다. 나를 가장 잘 설명하는 문장으로 직접 다듬어 주세요.", ids([...stamps, ...experiences])),
        makeSection("experience", "주요 경험", lines(experiences), ids(experiences)),
        makeSection("role", "내가 맡은 역할", "원본 기록에 역할이 없습니다. 학생이 실제로 맡은 역할을 직접 적어 주세요."),
        makeSection("actions", "내가 한 행동", "원본 기록에 구체적인 행동이 없습니다. 실제로 한 행동만 직접 적어 주세요."),
        makeSection("learning", "그 과정에서 배운 것", "활동을 돌아보고 학생이 직접 작성해 주세요."),
        makeSection("verified", "확인된 역량과 성취", lines([...competencies, ...achievements, ...stamps]), ids([...competencies, ...achievements, ...stamps])),
        makeSection("direction", "앞으로의 방향", goalText, ids(goals))
      ];
    }

    if (type === "ACTIVITY_RECORD") {
      const activityTitle = experiences.map((item) => item.title).join(", ") || "학생이 활동명을 입력해 주세요.";
      return [
        makeSection("activity", "활동명 · 기관 · 기간 · 활동유형", activityTitle + "\n" + experienceDetails(experiences), ids(experiences)),
        makeSection("role", "역할", "원본 기록에 역할이 없습니다. 맡은 역할을 학생이 직접 적어 주세요."),
        makeSection("activity-content", "활동 내용", lines(experiences), ids(experiences)),
        makeSection("my-actions", "내가 한 일", "실제로 수행한 일을 학생이 직접 적어 주세요."),
        makeSection("result", "결과", lines([...achievements, ...evidence]), ids([...achievements, ...evidence])),
        makeSection("learning", "배운 점", "활동 후 배운 점을 학생이 직접 작성해 주세요."),
        makeSection("competencies", "연결 역량과 성취", lines([...competencies, ...achievements]), ids([...competencies, ...achievements])),
        makeSection("evidence", "Evidence · Life Stamp", lines([...evidence, ...stamps]), ids([...evidence, ...stamps]))
      ];
    }

    if (type === "CAREER_PORTFOLIO") {
      return [
        makeSection("profile", "PROFILE", displayProfile),
        makeSection("career-question", "CAREER QUESTION", goalText, ids(goals)),
        makeSection("current-goal", "CURRENT GOAL", goalText, ids(goals)),
        makeSection("assessment", "ASSESSMENT", lines(assessments), ids(assessments)),
        makeSection("experiences", "EXPERIENCES", lines(experiences), ids(experiences)),
        makeSection("projects", "PROJECTS", lines(experiences.filter((item) => String(item.details.activityType || "").toUpperCase().includes("PROJECT"))), ids(experiences.filter((item) => String(item.details.activityType || "").toUpperCase().includes("PROJECT")))),
        makeSection("competencies", "COMPETENCIES", lines(competencies), ids(competencies)),
        makeSection("achievements", "ACHIEVEMENTS", lines(achievements), ids(achievements)),
        makeSection("stamps", "LIFE STAMPS", lines(stamps), ids(stamps)),
        makeSection("evidence", "EVIDENCE", lines(evidence), ids(evidence)),
        makeSection("next-step", "NEXT STEP", "다음에 실행할 구체적인 행동을 학생이 직접 적어 주세요.")
      ];
    }

    if (type === "PROJECT_PORTFOLIO") {
      return [
        makeSection("overview", "프로젝트 개요", lines(experiences), ids(experiences)),
        makeSection("problem", "문제 또는 목표", "원본 기록에 프로젝트 목표가 없습니다. 학생이 실제 목표를 적어 주세요."),
        makeSection("role", "나의 역할", "원본 기록에 역할이 없습니다. 학생이 실제 역할을 적어 주세요."),
        makeSection("process", "과정", experienceDetails(experiences), ids(experiences)),
        makeSection("problem-solving", "문제 해결", "실제 문제와 해결 과정을 학생이 직접 작성해 주세요."),
        makeSection("result", "결과", lines([...achievements, ...evidence]), ids([...achievements, ...evidence])),
        makeSection("competencies", "성장한 역량", lines(competencies), ids(competencies)),
        makeSection("stamp", "Achievement · Life Stamp", lines([...achievements, ...stamps]), ids([...achievements, ...stamps])),
        makeSection("reflection", "Reflection", "프로젝트를 돌아보고 학생이 직접 작성해 주세요.")
      ];
    }

    const competencyByName = new Map<string, { label: string; sources: PortfolioSource[] }>();
    competencies.forEach((item) => {
      const name = field(item.details.competencyName, item.title);
      const indicator = field(item.details.indicatorDescription, "");
      const key = indicator ? name + "::" + indicator : name + "::" + item.sourceId;
      const group = competencyByName.get(key);
      competencyByName.set(key, {
        label: indicator ? name + " · " + indicator : name,
        sources: [...(group?.sources || []), item]
      });
    });
    const changeLines: string[] = [];
    competencyByName.forEach(({ sources: items, label }) => {
      const ordered = [...items].sort((a, b) => new Date(a.occurredAt || 0).getTime() - new Date(b.occurredAt || 0).getTime());
      if (ordered.length < 2) {
        changeLines.push(label + ": 같은 지표의 이전 기록이 없어 변화량을 계산하지 않았습니다.");
      } else {
        const first = Number(ordered[0].details.score);
        const last = Number(ordered[ordered.length - 1].details.score);
        if (Number.isFinite(first) && Number.isFinite(last)) {
          const delta = last - first;
          changeLines.push(label + ": " + String(first) + " → " + String(last) + " (차이 " + (delta > 0 ? "+" : "") + String(delta) + ")");
        } else {
          changeLines.push(label + ": 점수 비교에 필요한 값이 없습니다.");
        }
      }
    });
    return [
      makeSection("period", "기간", periodText, ids(sources)),
      makeSection("experiences", "주요 경험", lines(experiences), ids(experiences)),
      makeSection("assessment", "주요 검사 결과", lines(assessments), ids(assessments)),
      makeSection("changes", "주요 변화", changeLines.length ? changeLines.join("\n") : "선택된 역량 기록이 없어 변화량을 계산하지 않았습니다."),
      makeSection("competencies", "Competency 변화", lines(competencies), ids(competencies)),
      makeSection("achievements", "Achievement", lines(achievements), ids(achievements)),
      makeSection("stamps", "Life Stamp", lines(stamps), ids(stamps)),
      makeSection("goal", "현재 목표", goalText, ids(goals)),
      makeSection("next-action", "다음 행동", "다음에 실행할 구체적인 행동을 학생이 직접 적어 주세요.")
    ];
  }
};

const optionalEnhancer: DocumentEnhancer | null = null;

function readPrefill(): Prefill | null {
  try {
    const raw = window.sessionStorage.getItem(PREFILL_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(PREFILL_KEY);
    const parsed = JSON.parse(raw) as Prefill;
    if (!parsed.sourceId || !parsed.sourceType) return null;
    return parsed;
  } catch {
    window.sessionStorage.removeItem(PREFILL_KEY);
    return null;
  }
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const element = document.createElement("textarea");
      element.value = value;
      element.style.position = "fixed";
      element.style.opacity = "0";
      document.body.appendChild(element);
      element.select();
      const success = document.execCommand("copy");
      element.remove();
      return success;
    } catch {
      return false;
    }
  }
}

function defaultPurposeForType(type: DocumentType): PortfolioPurpose {
  if (type === "PROJECT_PORTFOLIO") return "PROJECT_SHOWCASE";
  if (type === "GROWTH_REPORT") return "PERSONAL_GROWTH";
  if (type === "ACTIVITY_RECORD") return "SCHOOL_RECORD";
  if (type === "SELF_INTRODUCTION") return "INTERVIEW";
  return "MAJOR_EXPLORATION";
}

function isPortfolioPurpose(value: unknown): value is PortfolioPurpose {
  return PORTFOLIO_PURPOSES.some((item) => item.key === value);
}

function purposeLabel(value: PortfolioPurpose) {
  return PORTFOLIO_PURPOSES.find((item) => item.key === value)?.label || "포트폴리오";
}

function recommendationScore(source: PortfolioSource, type: DocumentType, purpose: PortfolioPurpose) {
  const base: Record<DocumentType, Partial<Record<SourceType, number>>> = {
    SELF_INTRODUCTION: { EXPERIENCE: 7, COMPETENCY: 6, ACHIEVEMENT: 6, LIFE_STAMP: 5, EVIDENCE: 4, GOAL: 3, ASSESSMENT_RESULT: 2 },
    ACTIVITY_RECORD: { EXPERIENCE: 9, EVIDENCE: 8, ACHIEVEMENT: 7, COMPETENCY: 5, LIFE_STAMP: 4, GOAL: 1, ASSESSMENT_RESULT: 1 },
    CAREER_PORTFOLIO: { EXPERIENCE: 7, GOAL: 7, COMPETENCY: 6, ASSESSMENT_RESULT: 5, ACHIEVEMENT: 5, LIFE_STAMP: 5, EVIDENCE: 4 },
    PROJECT_PORTFOLIO: { EXPERIENCE: 10, EVIDENCE: 9, ACHIEVEMENT: 7, COMPETENCY: 6, LIFE_STAMP: 5, GOAL: 2, ASSESSMENT_RESULT: 1 },
    GROWTH_REPORT: { COMPETENCY: 8, EXPERIENCE: 7, ACHIEVEMENT: 6, ASSESSMENT_RESULT: 6, GOAL: 5, LIFE_STAMP: 5, EVIDENCE: 4 }
  };
  const purposeBoost: Record<PortfolioPurpose, Partial<Record<SourceType, number>>> = {
    SCHOOL_RECORD: { EXPERIENCE: 3, ACHIEVEMENT: 2, LIFE_STAMP: 2, EVIDENCE: 2 },
    MAJOR_EXPLORATION: { ASSESSMENT_RESULT: 3, GOAL: 3, EXPERIENCE: 2, COMPETENCY: 2 },
    PROJECT_SHOWCASE: { EXPERIENCE: 4, EVIDENCE: 4, ACHIEVEMENT: 3, COMPETENCY: 2 },
    INTERVIEW: { EXPERIENCE: 3, COMPETENCY: 3, ACHIEVEMENT: 3, LIFE_STAMP: 2 },
    PERSONAL_GROWTH: { COMPETENCY: 3, ASSESSMENT_RESULT: 2, GOAL: 3, EXPERIENCE: 2 }
  };
  let score = (base[type][source.sourceType] || 0) + (purposeBoost[purpose][source.sourceType] || 0);
  const trust = String(source.trustStatus || "").toUpperCase();
  if (/TEACHER|VERIFIED|VALID|CONFIRMED|ISSUED/.test(trust)) score += 4;
  else if (!/SELF/.test(trust)) score += 2;
  const occurred = source.occurredAt ? new Date(source.occurredAt).getTime() : 0;
  if (occurred) {
    const ageDays = Math.max(0, (Date.now() - occurred) / 86400000);
    if (ageDays <= 365) score += 3;
    else if (ageDays <= 730) score += 2;
    else score += 1;
  }
  const activityType = String(source.details?.activityType || "").toUpperCase();
  const titleText = (source.title + " " + (source.description || "")).toUpperCase();
  if (type === "PROJECT_PORTFOLIO" && (/PROJECT/.test(activityType) || /프로젝트|PROJECT/.test(titleText))) score += 4;
  if (source.relatedSourceIds?.length) score += Math.min(2, source.relatedSourceIds.length);
  return score;
}

function smartSourceIds(sources: PortfolioSource[], type: DocumentType, purpose: PortfolioPurpose) {
  const limit = type === "CAREER_PORTFOLIO" || type === "GROWTH_REPORT" ? 12 : 8;
  return [...sources]
    .map((source) => ({ source, score: recommendationScore(source, type, purpose) }))
    .sort((a, b) => b.score - a.score || new Date(b.source.occurredAt || 0).getTime() - new Date(a.source.occurredAt || 0).getTime())
    .slice(0, Math.min(limit, sources.length))
    .map((item) => item.source.id);
}

function hasMeaningfulBody(section: Section) {
  const value = section.body.trim();
  if (value.length < 28) return false;
  return !/(직접 적어 주세요|직접 작성해 주세요|학생이 실제|원본 기록에 .*없습니다|선택된 기록이 없습니다)/.test(value);
}

function readinessFor(sections: Section[], sources: PortfolioSource[], meta: PortfolioMeta) {
  const visible = sections.filter((section) => section.visible);
  const meaningful = (pattern: RegExp) => visible.some((section) => pattern.test(section.title) && hasMeaningfulBody(section));
  const verifiedSource = sources.some((source) =>
    ["LIFE_STAMP", "EVIDENCE", "ACHIEVEMENT"].includes(source.sourceType)
    && !/SELF_REPORTED/i.test(source.trustStatus)
  );
  const checks: ReadinessCheck[] = [
    { label: "경험 근거", done: sources.some((source) => source.sourceType === "EXPERIENCE"), weight: 20, help: "실제 경험이 한 개 이상 연결되어야 합니다." },
    { label: "확인 가능한 증거", done: verifiedSource, weight: 20, help: "Life Stamp·증빙·확인된 성취 중 하나를 연결해 보세요." },
    { label: "나의 역할·행동", done: meaningful(/역할|행동|내가 한 일|과정|문제 해결/i), weight: 20, help: "내가 실제로 한 일을 구체적으로 적어 주세요." },
    { label: "배움·성찰", done: meaningful(/배운|성찰|Reflection|성장|변화/i), weight: 20, help: "경험을 통해 무엇이 달라졌는지 남겨 보세요." },
    { label: "목표·다음 행동", done: meaningful(/목표|방향|다음|Next/i) || sources.some((source) => source.sourceType === "GOAL"), weight: 10, help: "다음 행동 또는 현재 목표를 연결해 보세요." },
    { label: "사용 맥락", done: Boolean(meta.target.trim() || meta.headline.trim()), weight: 10, help: "누가 왜 볼 문서인지 한 줄로 정하면 훨씬 선명해집니다." }
  ];
  return {
    score: checks.reduce((total, item) => total + (item.done ? item.weight : 0), 0),
    checks
  };
}

function portfolioMarkdown(
  profile: LearnerProfile,
  title: string,
  type: DocumentType,
  meta: PortfolioMeta,
  sections: Section[],
  sources: PortfolioSource[]
) {
  const lines = [
    "# " + title,
    "",
    profile.displayName + " · " + typeLabel(type),
    purposeLabel(meta.purpose) + (meta.target ? " · " + meta.target : ""),
    meta.headline ? "> " + meta.headline : "",
    ""
  ];
  sections.filter((section) => section.visible).forEach((section) => {
    lines.push("## " + section.title, "", section.body.trim(), "");
  });
  if (sources.length) {
    lines.push("## 연결 기록", "");
    sources.forEach((source) => lines.push("- [" + sourceTypeLabel(source.sourceType) + "] " + source.title + " · " + source.trustLabel));
  }
  return lines.filter((line, index, all) => !(line === "" && all[index - 1] === "")).join("\n").trim() + "\n";
}

function safeFileName(value: string) {
  const cleaned = value.trim().replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ");
  return cleaned.slice(0, 80) || "portfolio";
}

function downloadTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function PortfolioStudioPage({ client }: { client: PlatformClient }) {
  const [catalog, setCatalog] = useState<CatalogPayload | null>(null);
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [mode, setMode] = useState<"home" | "editor">("home");
  const [stage, setStage] = useState<"sources" | "edit">("sources");
  const [documentType, setDocumentType] = useState<DocumentType>("SELF_INTRODUCTION");
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [sections, setSections] = useState<Section[]>([]);
  const [purpose, setPurpose] = useState<PortfolioPurpose>("MAJOR_EXPLORATION");
  const [target, setTarget] = useState("");
  const [headline, setHeadline] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [editingVersion, setEditingVersion] = useState(0);
  const [documentStatus, setDocumentStatus] = useState<"DRAFT" | "ARCHIVED">("DRAFT");
  const [dirty, setDirty] = useState(false);
  const [sharePreview, setSharePreview] = useState(false);
  const [includeStamps, setIncludeStamps] = useState(true);
  const [includeExperiences, setIncludeExperiences] = useState(true);
  const [latestShare, setLatestShare] = useState<{ shareUrl: string; expiresAt: string } | null>(null);
  const [aiBusy, setAiBusy] = useState("");
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  const [aiAvailable, setAiAvailable] = useState<boolean | null>(null);
  const [aiModel, setAiModel] = useState("");
  const [prefill, setPrefill] = useState<Prefill | null>(() => readPrefill());

  useEffect(() => {
    let active = true;
    Promise.all([
      client.rpc("wallet_portfolio_sources"),
      client.rpc("wallet_experience_portfolio_sources"),
      client.rpc("wallet_portfolio_documents")
    ]).then(([sourceResult, experienceSourceResult, documentResult]) => {
      if (!active) return;
      if (sourceResult.error) {
        setError(sourceResult.error.message);
        setLoading(false);
        return;
      }
      if (experienceSourceResult.error) {
        setError(experienceSourceResult.error.message);
        setLoading(false);
        return;
      }
      if (documentResult.error) setError(documentResult.error.message);
      const baseCatalog = sourceResult.data as CatalogPayload;
      const extendedCatalog = experienceSourceResult.data as CatalogPayload;
      const merged = new Map<string, PortfolioSource>();
      [...(baseCatalog.sources || []), ...(extendedCatalog.sources || [])].forEach((source) => {
        const current = merged.get(source.id);
        if (!current) {
          merged.set(source.id, source);
          return;
        }
        merged.set(source.id, {
          ...current,
          ...source,
          details: { ...current.details, ...source.details },
          relatedSourceIds: [...new Set([...current.relatedSourceIds, ...source.relatedSourceIds])]
        });
      });
      setCatalog({ learner: baseCatalog.learner, sources: [...merged.values()] });
      setLibrary((documentResult.data || []) as LibraryItem[]);
      setLoading(false);
    }).catch((reason) => {
      if (!active) return;
      setError(reason instanceof Error ? reason.message : "기록을 불러오지 못했습니다.");
      setLoading(false);
    });
    return () => { active = false; };
  }, [client]);

  useEffect(() => {
    let active = true;
    client.functions.invoke("wallet-ai-writing", { body: { action: "STATUS" } }).then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setAiAvailable(false);
        return;
      }
      const result = (data || {}) as { configured?: boolean; model?: string | null };
      setAiAvailable(Boolean(result.configured));
      setAiModel(typeof result.model === "string" ? result.model : "");
    }).catch(() => {
      if (active) setAiAvailable(false);
    });
    return () => { active = false; };
  }, [client]);

  useEffect(() => {
    if (!catalog || !prefill) return;
    const found = catalog.sources.some((source) => source.sourceType === prefill.sourceType && source.sourceId === prefill.sourceId);
    if (!found) {
      setNotice("선택한 원본 기록이 현재 사용할 수 없어 기록 목록에서 직접 선택해 주세요.");
      setMode("editor");
      setStage("sources");
      setDocumentType(prefill.documentType || "SELF_INTRODUCTION");
      setSelectedIds(new Set());
      setTitle(typeLabel(prefill.documentType || "SELF_INTRODUCTION"));
      setPrefill(null);
      return;
    }
    const type = prefill.documentType || (prefill.sourceType === "EXPERIENCE" ? "ACTIVITY_RECORD" : "SELF_INTRODUCTION");
    setDocumentType(type);
    setTitle(typeLabel(type));
    setSelectedIds(new Set([sourceKey(prefill.sourceType, prefill.sourceId)]));
    setMode("editor");
    setStage("sources");
    setNotice("선택한 기록을 불러왔습니다. 연결 추천 기록은 자동 선택되지 않습니다.");
    setPrefill(null);
  }, [catalog, prefill]);

  const selectedSources = useMemo(
    () => (catalog?.sources || []).filter((source) => selectedIds.has(source.id)),
    [catalog, selectedIds]
  );

  const portfolioMeta = useMemo<PortfolioMeta>(() => ({ purpose, target, headline }), [purpose, target, headline]);
  const readiness = useMemo(
    () => readinessFor(sections, selectedSources, portfolioMeta),
    [sections, selectedSources, portfolioMeta]
  );
  const submissionMarkdown = useMemo(
    () => portfolioMarkdown(catalog?.learner || { learnerId: "", displayName: "" }, title, documentType, portfolioMeta, sections, selectedSources),
    [catalog, title, documentType, portfolioMeta, sections, selectedSources]
  );

  const recommendedIds = useMemo(() => {
    const result = new Set<string>();
    selectedSources.filter((source) => source.sourceType === "LIFE_STAMP").forEach((source) => {
      source.relatedSourceIds.forEach((id) => result.add(id));
    });
    return result;
  }, [selectedSources]);

  const refreshLibrary = async () => {
    const { data, error: loadError } = await client.rpc("wallet_portfolio_documents");
    if (loadError) {
      setError(loadError.message);
      return;
    }
    setLibrary((data || []) as LibraryItem[]);
  };

  function startCreate(type: DocumentType) {
    setDocumentType(type);
    setDocumentId(null);
    setCurrentVersion(0);
    setEditingVersion(0);
    setVersions([]);
    setDocumentStatus("DRAFT");
    setTitle(typeLabel(type));
    setSections([]);
    setPurpose(defaultPurposeForType(type));
    setTarget("");
    setHeadline("");
    setSelectedIds(new Set());
    setLatestShare(null);
    setDirty(false);
    setSharePreview(false);
    setStage("sources");
    setMode("editor");
    setError("");
    setNotice("");
  }

  function backHome() {
    setMode("home");
    setStage("sources");
    setSharePreview(false);
    setLatestShare(null);
    setError("");
    setNotice("");
    void refreshLibrary();
  }

  function toggleSource(source: PortfolioSource) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(source.id)) next.delete(source.id);
      else next.add(source.id);
      return next;
    });
    setDirty(true);
  }

  function beginWriting() {
    if (!selectedSources.length) {
      setError("문서에 사용할 기록을 한 개 이상 선택해 주세요.");
      return;
    }
    const created = deterministicDocumentComposer.compose({
      type: documentType,
      profile: catalog?.learner || { learnerId: "", displayName: "" },
      sources: selectedSources
    });
    setSections(created);
    setStage("edit");
    setDirty(true);
    setError("");
    setNotice("원본 기록을 바탕으로 초안을 만들었습니다. 빈 역할·행동·성찰 항목은 학생이 직접 작성해 주세요.");
  }

  async function regenerate() {
    if (!selectedSources.length) {
      setError("먼저 문서에 사용할 기록을 선택해 주세요.");
      setStage("sources");
      return;
    }
    if (!window.confirm("현재 편집한 문장과 섹션이 새 초안으로 바뀝니다. 다시 생성할까요?")) return;
    setBusy(true);
    let created = deterministicDocumentComposer.compose({
      type: documentType,
      profile: catalog?.learner || { learnerId: "", displayName: "" },
      sources: selectedSources
    });
    if (optionalEnhancer) created = await optionalEnhancer.enhance(created);
    setSections(created);
    setDirty(true);
    setBusy(false);
    setNotice("선택한 원본 기록으로 다시 생성했습니다. 모든 문장은 학생이 확인하고 수정할 수 있습니다.");
  }

  async function runAiWriting(action: AiWritingAction, baseSections: Section[] = sections, sectionIndex?: number) {
    if (!selectedSources.length) {
      setError("AI 글쓰기에 사용할 기록을 한 개 이상 선택해 주세요.");
      setStage("sources");
      return false;
    }
    if (!baseSections.length) {
      setError("먼저 문서 초안을 만들어 주세요.");
      return false;
    }
    if (documentStatus === "ARCHIVED") return false;

    const targetSections = sectionIndex === undefined ? baseSections : [baseSections[sectionIndex]];
    if (!targetSections.length || targetSections.some((section) => !section)) return false;

    setAiBusy(action + ":" + String(sectionIndex ?? "ALL"));
    setError("");
    setNotice("");
    setAiWarnings([]);

    const { data, error: aiError } = await client.functions.invoke("wallet-ai-writing", {
      body: {
        action,
        documentType,
        purpose,
        target,
        headline,
        sources: selectedSources.map((source) => ({
          sourceType: source.sourceType,
          sourceId: source.sourceId
        })),
        sections: targetSections.map((section) => ({
          key: section.key,
          title: section.title,
          body: section.body
        }))
      }
    });

    setAiBusy("");

    if (aiError) {
      setError("AI 글쓰기를 사용할 수 없습니다. AI 연결 설정 또는 네트워크 상태를 확인해 주세요.");
      return false;
    }

    const result = (data || {}) as AiWritingResponse;
    if (!Array.isArray(result.sections) || !result.sections.length) {
      setError(result.message || "AI 글쓰기 결과를 불러오지 못했습니다.");
      return false;
    }

    if (sectionIndex === undefined) {
      const byKey = new Map(result.sections.map((section) => [section.key, section]));
      setSections(baseSections.map((section) => {
        const rewritten = byKey.get(section.key);
        return rewritten ? { ...section, title: rewritten.title, body: rewritten.body } : section;
      }));
    } else {
      const rewritten = result.sections[0];
      setSections((current) => current.map((section, index) =>
        index === sectionIndex ? { ...section, title: rewritten.title, body: rewritten.body } : section
      ));
    }

    setDirty(true);
    setAiWarnings(Array.isArray(result.warnings) ? result.warnings : []);
    const actionLabels: Record<AiWritingAction, string> = {
      FULL_DRAFT: "AI 초안",
      POLISH: "문장 다듬기",
      MORE_SPECIFIC: "구체화",
      SHORTEN: "간결화",
      STUDENT_TONE: "학생 말투",
      INTERVIEW_TONE: "면접용 변환",
      ADMISSION_TONE: "진학·학과용 변환"
    };
    setNotice(actionLabels[action] + "을 반영했습니다. 원본 기록과 사실이 맞는지 학생이 직접 확인한 뒤 저장해 주세요.");
    setStage("edit");
    return true;
  }

  async function beginAiWriting() {
    if (!selectedSources.length) {
      setError("AI 글쓰기에 사용할 기록을 한 개 이상 선택해 주세요.");
      return;
    }
    const created = deterministicDocumentComposer.compose({
      type: documentType,
      profile: catalog?.learner || { learnerId: "", displayName: "" },
      sources: selectedSources
    });
    setSections(created);
    setStage("edit");
    setDirty(true);
    await runAiWriting("FULL_DRAFT", created);
  }

  function updateSection(index: number, changes: Partial<Section>) {
    setSections((current) => current.map((section, position) => position === index ? { ...section, ...changes } : section));
    setDirty(true);
  }

  function moveSection(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    setSections((current) => {
      const next = [...current];
      const picked = next[index];
      next[index] = next[target];
      next[target] = picked;
      return next;
    });
    setDirty(true);
  }

  function addSection() {
    setSections((current) => [
      ...current,
      makeSection("custom-" + String(Date.now()), "새 섹션", "학생이 직접 작성해 주세요.")
    ]);
    setDirty(true);
  }

  function applySmartSelection() {
    const ids = smartSourceIds(catalog?.sources || [], documentType, purpose);
    setSelectedIds(new Set(ids));
    setDirty(true);
    setError("");
    setNotice(ids.length
      ? "문서 유형과 목적, 확인 상태, 최근성을 기준으로 추천 기록 " + String(ids.length) + "개를 골랐습니다. 필요 없는 기록은 해제해 주세요."
      : "추천할 기록이 없습니다. 경험이나 Life Stamp를 먼저 추가해 주세요.");
  }

  function insertSectionTemplate(templateKey: string) {
    const template = SECTION_TEMPLATES.find((item) => item.key === templateKey);
    if (!template) return;
    setSections((current) => [
      ...current,
      makeSection("template-" + template.key + "-" + String(Date.now()), template.title, template.body)
    ]);
    setDirty(true);
    setNotice(template.label + " 섹션을 추가했습니다. 질문 문장을 실제 경험으로 바꿔 주세요.");
  }

  async function copySubmissionText() {
    const success = await copyText(submissionMarkdown);
    setNotice(success ? "제출용 텍스트를 복사했습니다." : "텍스트를 복사하지 못했습니다.");
  }

  function downloadSubmissionMarkdown() {
    downloadTextFile(safeFileName(title) + ".md", submissionMarkdown);
    setNotice("Markdown 파일을 저장했습니다.");
  }

  function printSubmission() {
    window.print();
  }

  async function openDocument(id: string, versionNo?: number) {
    setBusy(true);
    setError("");
    setNotice("");
    const { data, error: loadError } = await client.rpc("wallet_portfolio_document", {
      p_document_id: id,
      p_version_no: versionNo ?? null
    });
    setBusy(false);
    if (loadError) {
      setError(loadError.message);
      return;
    }
    const item = data as DocumentPayload;
    setDocumentId(item.documentId);
    setDocumentType(item.documentType);
    setTitle(item.title);
    setSections(Array.isArray(item.content?.sections) ? item.content.sections : []);
    const savedMeta = item.content?.portfolioMeta;
    setPurpose(isPortfolioPurpose(savedMeta?.purpose) ? savedMeta.purpose : defaultPurposeForType(item.documentType));
    setTarget(typeof savedMeta?.target === "string" ? savedMeta.target : "");
    setHeadline(typeof savedMeta?.headline === "string" ? savedMeta.headline : "");
    setSelectedIds(new Set((item.sources || []).map((source) => source.id)));
    setVersions(item.versions || []);
    setCurrentVersion(item.currentVersion);
    setEditingVersion(item.versionNo);
    setDocumentStatus(item.status);
    setDirty(false);
    setLatestShare(null);
    setSharePreview(false);
    setStage("edit");
    setMode("editor");
    if (versionNo && versionNo !== item.currentVersion) {
      setNotice("이전 버전 v" + String(versionNo) + "을 보고 있습니다. 저장하면 새 버전으로 추가됩니다.");
    }
  }

  async function saveDocument() {
    if (!title.trim()) {
      setError("문서 제목을 입력해 주세요.");
      return;
    }
    if (!sections.length) {
      setError("먼저 기록을 선택해 문서 초안을 만들어 주세요.");
      setStage("sources");
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    const { data, error: saveError } = await client.rpc("wallet_portfolio_save_document", {
      p_document_id: documentId,
      p_document_type: documentType,
      p_title: title.trim(),
      p_content: { schemaVersion: 2, sections, portfolioMeta },
      p_sources: selectedSources.map((source) => ({
        sourceType: source.sourceType,
        sourceId: source.sourceId
      }))
    });
    setBusy(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    const saved = data as { documentId: string; currentVersion: number; title: string };
    const now = new Date().toISOString();
    setDocumentId(saved.documentId);
    setCurrentVersion(saved.currentVersion);
    setEditingVersion(saved.currentVersion);
    setDocumentStatus("DRAFT");
    setDirty(false);
    setVersions((current) => [
      { versionNo: saved.currentVersion, createdAt: now },
      ...current.filter((item) => item.versionNo !== saved.currentVersion)
    ].sort((a, b) => b.versionNo - a.versionNo));
    setNotice("v" + String(saved.currentVersion) + "으로 저장했습니다. 이전 버전은 보존됩니다.");
    await refreshLibrary();
  }

  async function archiveDocument(item: LibraryItem) {
    if (!window.confirm("이 문서를 보관할까요? 기존 버전은 보존되고 활성 공유 링크는 취소됩니다.")) return;
    setBusy(true);
    const { error: archiveError } = await client.rpc("wallet_portfolio_archive_document", {
      p_document_id: item.documentId
    });
    setBusy(false);
    if (archiveError) {
      setError(archiveError.message);
      return;
    }
    if (documentId === item.documentId) setDocumentStatus("ARCHIVED");
    setNotice("문서를 보관했습니다. 활성 공유 링크도 취소했습니다.");
    await refreshLibrary();
  }

  async function createShare() {
    if (!documentId || dirty || documentStatus === "ARCHIVED") {
      setError("공유 전 문서를 먼저 저장해 주세요.");
      return;
    }
    setBusy(true);
    setError("");
    const { data, error: shareError } = await client.rpc("wallet_portfolio_create_share", {
      p_document_id: documentId,
      p_version_no: currentVersion,
      p_include_stamps: includeStamps,
      p_include_experiences: includeExperiences,
      p_valid_days: 30
    });
    setBusy(false);
    if (shareError) {
      setError(shareError.message);
      return;
    }
    const created = data as { shareUrl: string; expiresAt: string };
    setLatestShare(created);
    setSharePreview(false);
    setNotice("저장한 문서 버전으로 30일 공유 링크를 만들었습니다.");
    await refreshLibrary();
  }

  async function revokeShare(shareId: string) {
    setBusy(true);
    const { error: revokeError } = await client.rpc("wallet_revoke_share", { p_share_id: shareId });
    setBusy(false);
    if (revokeError) {
      setError(revokeError.message);
      return;
    }
    setLatestShare(null);
    setNotice("공유 링크를 취소했습니다.");
    await refreshLibrary();
  }

  const groupedSources = useMemo(() => SOURCE_TYPES.map((type) => ({
    ...type,
    items: (catalog?.sources || []).filter((source) => source.sourceType === type.key)
  })), [catalog]);

  if (loading) return <section className="portfolio-studio-root"><p className="muted">문서 스튜디오를 준비하는 중…</p></section>;
  if (!catalog) return <section className="portfolio-studio-root"><p className="error">{error || "기록을 불러오지 못했습니다."}</p></section>;

  return (
    <section className="portfolio-studio-root">
      <header className="portfolio-studio-heading">
        <div className="portfolio-studio-brand"><span>RS</span><strong>LIFE PASSPORT</strong><i>PORTFOLIO STUDIO</i></div>
        <p className="eyebrow">기록에서 문서로</p>
        <h2>만들기</h2>
        <p>기록을 고르고, 학생이 직접 다듬고, 버전으로 저장합니다.</p>
      </header>

      {error && <p className="error" role="alert">{error}</p>}
      {notice && <p className="portfolio-notice" role="status">{notice}</p>}

      <div className="portfolio-print-only" aria-hidden="true">
        <PublicDocumentPreview
          profile={catalog.learner}
          title={title || typeLabel(documentType)}
          type={documentType}
          sections={sections}
          sources={selectedSources}
          includeStamps
          includeExperiences
          purpose={purpose}
          target={target}
          headline={headline}
        />
      </div>

      {mode === "home" ? (
        <>
          <section className="portfolio-type-section">
            <div className="portfolio-subheading">
              <div><p className="eyebrow">새 문서 만들기</p><h3>무엇을 만들까요?</h3></div>
              <span>선택한 기록만 사용</span>
            </div>
            <div className="portfolio-type-grid">
              {DOCUMENT_TYPES.map((item) => (
                <button className="portfolio-type-card" key={item.key} onClick={() => startCreate(item.key)}>
                  <span className="portfolio-type-number">{item.icon}</span>
                  <span className="portfolio-type-copy"><strong>{item.title}</strong><small>{item.description}</small></span>
                  <span className="portfolio-card-arrow">→</span>
                </button>
              ))}
            </div>
          </section>

          <section className="portfolio-library">
            <div className="portfolio-subheading">
              <div><p className="eyebrow">DOCUMENT LIBRARY</p><h3>내 문서</h3></div>
              <span>{library.length}개</span>
            </div>
            {library.length ? (
              <div className="portfolio-library-list">
                {library.map((item) => (
                  <article className="portfolio-library-card" key={item.documentId}>
                    <div className="portfolio-library-main">
                      <div className="portfolio-document-icon">문서</div>
                      <div>
                        <p className="portfolio-document-type">{typeLabel(item.documentType)}</p>
                        <h4>{item.title}</h4>
                        <p className="portfolio-document-meta">
                          v{item.currentVersion} · 기록 {item.sourceCount}개 · 수정 {shortDate(item.updatedAt)}
                        </p>
                      </div>
                      <span className={item.status === "ARCHIVED" ? "portfolio-status archived" : "portfolio-status"}>
                        {item.status === "ARCHIVED" ? "보관" : "초안"}
                      </span>
                    </div>
                    <div className="portfolio-library-actions">
                      <button className="secondary" onClick={() => void openDocument(item.documentId)}>열기</button>
                      <button className="secondary" disabled={busy || item.status === "ARCHIVED"} onClick={() => void openDocument(item.documentId).then(() => {
                        setDocumentId(null);
                        setCurrentVersion(0);
                        setEditingVersion(0);
                        setVersions([]);
                        setLatestShare(null);
                        setSharePreview(false);
                        setDirty(false);
                        setTitle((current) => current + " (복사)");
                        setNotice("복제본을 편집 중입니다. 저장하면 새 문서로 등록됩니다.");
                      })}>복제</button>
                      {item.status === "DRAFT" && <button className="secondary danger" disabled={busy} onClick={() => void archiveDocument(item)}>보관</button>}
                    </div>
                    {item.shareLinks?.length > 0 && (
                      <div className="portfolio-share-links">
                        <strong>공유 링크</strong>
                        {item.shareLinks.map((share) => (
                          <div className="portfolio-share-link-row" key={share.shareId}>
                            <span>{share.status === "ACTIVE" ? "공유 중" : share.status === "EXPIRED" ? "만료" : "취소"} · 만료 {shortDate(share.expiresAt)}</span>
                            <div>
                              {share.status === "ACTIVE" && <button className="text-button" onClick={() => void copyText(share.shareUrl)}>링크 복사</button>}
                              {share.status === "ACTIVE" && <button className="text-button danger-text" disabled={busy} onClick={() => void revokeShare(share.shareId)}>취소</button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <div className="portfolio-empty"><strong>아직 저장한 문서가 없습니다.</strong><span>Life Stamp나 경험을 골라 첫 문서를 만들어 보세요.</span></div>
            )}
          </section>
        </>
      ) : (
        <div className="portfolio-editor-shell">
          <div className="portfolio-stepbar">
            <button className={stage === "sources" ? "active" : ""} onClick={() => setStage("sources")}>1. 기록 선택</button>
            <span>→</span>
            <button className={stage === "edit" ? "active" : ""} disabled={!sections.length} onClick={() => setStage("edit")}>2. 문서 편집</button>
            <button className="portfolio-back-button" onClick={backHome}>내 문서로</button>
          </div>

          {stage === "sources" ? (
            <section className="portfolio-source-stage">
              <div className="portfolio-editor-title-row">
                <div><p className="eyebrow">SOURCE SELECTOR</p><h3>{typeLabel(documentType)}에 사용할 기록</h3></div>
                <span>{selectedIds.size}개 선택</span>
              </div>
              <label className="portfolio-title-field">문서 제목
                <input value={title} maxLength={160} onChange={(event) => { setTitle(event.target.value); setDirty(true); }} />
              </label>

              <section className="portfolio-strategy-panel">
                <div className="portfolio-strategy-head">
                  <div><p className="eyebrow">PORTFOLIO PURPOSE</p><h4>누가 왜 볼 문서인지 먼저 정하기</h4></div>
                  <button className="secondary" type="button" onClick={applySmartSelection}>추천 기록 자동 선택</button>
                </div>
                <div className="portfolio-purpose-grid">
                  {PORTFOLIO_PURPOSES.map((item) => (
                    <button
                      type="button"
                      key={item.key}
                      className={purpose === item.key ? "portfolio-purpose-card active" : "portfolio-purpose-card"}
                      onClick={() => { setPurpose(item.key); setDirty(true); }}
                    >
                      <strong>{item.label}</strong>
                      <span>{item.help}</span>
                    </button>
                  ))}
                </div>
                <div className="portfolio-strategy-fields">
                  <label>사용 대상·상황
                    <input
                      value={target}
                      maxLength={120}
                      placeholder="예: 공학계열 탐색, 교내 프로젝트 발표, 면접 준비"
                      onChange={(event) => { setTarget(event.target.value); setDirty(true); }}
                    />
                  </label>
                  <label>한 줄 소개
                    <input
                      value={headline}
                      maxLength={180}
                      placeholder="예: 관찰한 문제를 직접 실험하고 개선하는 학생"
                      onChange={(event) => { setHeadline(event.target.value); setDirty(true); }}
                    />
                  </label>
                </div>
              </section>

              <p className="portfolio-trust-note">모든 기록을 자동으로 넣지 않습니다. 확인 상태를 살펴보고 사용할 기록을 직접 선택하세요.</p>
              <div className="portfolio-source-groups">
                {groupedSources.map((group) => (
                  <section className="portfolio-source-group" key={group.key}>
                    <div className="portfolio-source-group-heading"><div><h4>{group.label}</h4><p>{group.help}</p></div><span>{group.items.length}</span></div>
                    {group.items.length ? (
                      <div className="portfolio-source-list">
                        {group.items.map((source) => {
                          const selected = selectedIds.has(source.id);
                          const recommended = recommendedIds.has(source.id) && !selected;
                          return (
                            <label className={selected ? "portfolio-source-card selected" : "portfolio-source-card"} key={source.id}>
                              <input type="checkbox" checked={selected} onChange={() => toggleSource(source)} />
                              <span className="portfolio-source-main">
                                <span className="portfolio-source-title-row"><strong>{source.title}</strong>{recommended && <em>Life Stamp 연결 추천</em>}</span>
                                <small>{source.description || source.trustLabel}</small>
                                <span className="portfolio-source-meta">{formatDate(source.occurredAt)} · <b>{source.trustLabel}</b></span>
                                {source.sourceType === "ASSESSMENT_RESULT" && <span className="portfolio-source-caution">자기이해를 돕는 자료입니다. 직업이나 진로를 확정하지 않습니다.</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : <p className="portfolio-empty-inline">선택할 기록이 없습니다.</p>}
                  </section>
                ))}
              </div>
              <section className="portfolio-ai-entry">
                <div>
                  <span className="portfolio-ai-orb" aria-hidden="true">AI</span>
                  <div>
                    <p className="eyebrow">AI WRITING</p>
                    <strong>선택한 기록만 근거로 AI 초안 만들기</strong>
                    <small>이름·학교·이메일은 AI로 보내지 않습니다. AI가 쓴 문장은 반드시 사실을 확인한 뒤 저장하세요.</small>
                  </div>
                </div>
                <button
                  className="portfolio-ai-primary"
                  type="button"
                  disabled={!selectedIds.size || Boolean(aiBusy) || aiAvailable !== true}
                  onClick={() => void beginAiWriting()}
                >
                  {aiBusy ? "AI가 작성 중…" : aiAvailable === false ? "AI 연결 준비 중" : aiAvailable === null ? "AI 상태 확인 중…" : "✦ AI로 초안 만들기"}
                </button>
              </section>
              <div className="portfolio-stage-actions">
                <button className="secondary" onClick={backHome}>취소</button>
                <button className="secondary" disabled={!selectedIds.size || Boolean(aiBusy)} onClick={beginWriting}>
                  {sections.length ? "규칙형 초안 다시 만들기" : "AI 없이 기본 초안 만들기"}
                </button>
              </div>
            </section>
          ) : (
            <>
              <div className="portfolio-editor-toolbar">
                <div>
                  <p className="eyebrow">{typeLabel(documentType)}{documentId ? " · v" + String(currentVersion) : " · 새 문서"}</p>
                  <h3>{documentStatus === "ARCHIVED" ? "보관 문서" : dirty ? "편집 중" : "문서 미리보기"}</h3>
                </div>
                <div className="portfolio-toolbar-actions">
                  <button className="secondary" onClick={() => { setStage("sources"); setSharePreview(false); }}>기록 추가·제거</button>
                  <button className="secondary" disabled={!sections.length} onClick={() => void copySubmissionText()}>텍스트 복사</button>
                  <button className="secondary" disabled={!sections.length} onClick={downloadSubmissionMarkdown}>Markdown</button>
                  <button className="secondary" disabled={!sections.length} onClick={printSubmission}>PDF·인쇄</button>
                  <button className="secondary" disabled={!documentId || dirty || documentStatus === "ARCHIVED"} onClick={() => setSharePreview((value) => !value)}>공유 미리보기</button>
                  <button className="primary" disabled={busy || documentStatus === "ARCHIVED"} onClick={() => void saveDocument()}>
                    {busy ? "저장 중…" : documentId ? "새 버전 저장" : "문서 저장"}
                  </button>
                </div>
              </div>

              {documentId && versions.length > 0 && (
                <div className="portfolio-version-strip">
                  <span>버전</span>
                  {versions.map((version) => (
                    <button key={version.versionNo} className={version.versionNo === editingVersion ? "active" : ""} disabled={busy} onClick={() => void openDocument(documentId, version.versionNo)}>
                      v{version.versionNo} · {shortDate(version.createdAt)}
                    </button>
                  ))}
                </div>
              )}
              {editingVersion > 0 && editingVersion !== currentVersion && (
                <p className="portfolio-history-note">이전 버전 v{editingVersion}을 보고 있습니다. 편집 후 저장하면 새 버전으로 추가됩니다.</p>
              )}

              {sharePreview && (
                <section className="portfolio-share-preview">
                  <div className="portfolio-share-preview-head">
                    <div><p className="eyebrow">PUBLIC PREVIEW</p><h4>공유 화면 미리보기</h4></div>
                    <button className="text-button" onClick={() => setSharePreview(false)}>닫기</button>
                  </div>
                  <div className="portfolio-share-scopes">
                    <label><input type="checkbox" checked disabled /> 저장된 문서 본문</label>
                    <label><input type="checkbox" checked={includeStamps} onChange={(event) => setIncludeStamps(event.target.checked)} /> 선택한 Life Stamp</label>
                    <label><input type="checkbox" checked={includeExperiences} onChange={(event) => setIncludeExperiences(event.target.checked)} /> 선택한 경험</label>
                  </div>
                  <PublicDocumentPreview
                    profile={catalog.learner}
                    title={title}
                    type={documentType}
                    sections={sections}
                    sources={selectedSources}
                    includeStamps={includeStamps}
                    includeExperiences={includeExperiences}
                    purpose={purpose}
                    target={target}
                    headline={headline}
                  />
                  <p className="portfolio-trust-note">공유 링크에는 이 문서 버전과 위에서 선택한 Life Stamp·경험만 포함됩니다. 30일 후 만료되며 기존 Wallet 공유 취소 기능으로 취소할 수 있습니다.</p>
                  {dirty && <p className="portfolio-source-caution">저장되지 않은 변경이 있습니다. 저장한 뒤 공유 링크를 만들 수 있습니다.</p>}
                  <button className="primary" disabled={busy || dirty || !documentId} onClick={() => void createShare()}>
                    {busy ? "공유 링크 생성 중…" : "이 버전의 공유 링크 만들기"}
                  </button>
                </section>
              )}

              {latestShare && (
                <div className="portfolio-created-share">
                  <div><strong>공유 링크가 준비되었습니다.</strong><span>만료 {formatDate(latestShare.expiresAt)}</span><code>{latestShare.shareUrl}</code></div>
                  <button className="secondary" onClick={() => void copyText(latestShare.shareUrl)}>링크 복사</button>
                  <a className="secondary-link" href={latestShare.shareUrl} target="_blank" rel="noreferrer">공개 문서 보기</a>
                </div>
              )}

              <div className="portfolio-edit-layout">
                <main className="portfolio-section-editor">
                  <label className="portfolio-title-field">문서 제목
                    <input value={title} maxLength={160} disabled={documentStatus === "ARCHIVED"} onChange={(event) => { setTitle(event.target.value); setDirty(true); }} />
                  </label>
                  <div className="portfolio-edit-note"><strong>학생이 직접 확인하고 고쳐 주세요.</strong><span>AI도 선택한 원본 기록만 참고합니다. 원본에 없는 역할·수치·성과는 사실로 추가하지 않으며, 저장 전 학생이 최종 확인합니다.</span></div>
                  {aiWarnings.length > 0 && (
                    <div className="portfolio-ai-warning">
                      <strong>AI 확인 메모</strong>
                      {aiWarnings.map((warning, index) => <span key={String(index) + warning}>{warning}</span>)}
                    </div>
                  )}
                  {sections.map((section, index) => (
                    <article className={section.visible ? "portfolio-section-card" : "portfolio-section-card hidden-section"} key={section.key}>
                      <div className="portfolio-section-head">
                        <span className="portfolio-section-order">{String(index + 1).padStart(2, "0")}</span>
                        <input value={section.title} aria-label="섹션 제목" disabled={documentStatus === "ARCHIVED"} onChange={(event) => updateSection(index, { title: event.target.value })} />
                        <div className="portfolio-section-actions">
                          <button aria-label="위로 이동" disabled={index === 0 || documentStatus === "ARCHIVED" || Boolean(aiBusy)} onClick={() => moveSection(index, -1)}>↑</button>
                          <button aria-label="아래로 이동" disabled={index === sections.length - 1 || documentStatus === "ARCHIVED" || Boolean(aiBusy)} onClick={() => moveSection(index, 1)}>↓</button>
                          <button aria-pressed={!section.visible} disabled={documentStatus === "ARCHIVED" || Boolean(aiBusy)} onClick={() => updateSection(index, { visible: !section.visible })}>{section.visible ? "숨기기" : "표시"}</button>
                        </div>
                      </div>
                      <textarea value={section.body} disabled={documentStatus === "ARCHIVED" || Boolean(aiBusy)} rows={Math.max(4, Math.min(14, section.body.split("\n").length + 2))} onChange={(event) => updateSection(index, { body: event.target.value })} />
                      <div className="portfolio-section-ai-actions">
                        <span>AI</span>
                        <button disabled={Boolean(aiBusy) || documentStatus === "ARCHIVED" || aiAvailable !== true} onClick={() => void runAiWriting("POLISH", sections, index)}>문장 다듬기</button>
                        <button disabled={Boolean(aiBusy) || documentStatus === "ARCHIVED" || aiAvailable !== true} onClick={() => void runAiWriting("MORE_SPECIFIC", sections, index)}>더 구체적으로</button>
                        <button disabled={Boolean(aiBusy) || documentStatus === "ARCHIVED" || aiAvailable !== true} onClick={() => void runAiWriting("SHORTEN", sections, index)}>짧고 선명하게</button>
                      </div>
                      {section.sourceIds.length > 0 && <small className="portfolio-section-sources">연결 원본 {section.sourceIds.length}개 · 해당 기록의 확인 상태는 원본 자료에서 표시됩니다.</small>}
                    </article>
                  ))}
                  <div className="portfolio-bottom-actions">
                    <button className="secondary" disabled={documentStatus === "ARCHIVED"} onClick={addSection}>섹션 추가</button>
                    <button className="secondary" disabled={busy || documentStatus === "ARCHIVED"} onClick={() => void regenerate()}>다시 생성</button>
                  </div>
                </main>

                <aside className="portfolio-record-rail">
                  <section className="portfolio-ai-assistant">
                    <div className="portfolio-ai-assistant-head">
                      <span className="portfolio-ai-orb" aria-hidden="true">AI</span>
                      <div>
                        <p className="eyebrow">AI WRITING COACH</p>
                        <strong>전체 글 다듬기</strong>
                      </div>
                    </div>
                    <p>현재 문서 구조와 연결 원본은 그대로 두고 문장만 개선합니다.</p>
                    <div className="portfolio-ai-status-line">
                      <span className={aiAvailable ? "ready" : ""}>{aiAvailable ? "AI 연결됨" : "AI 연결 준비 중"}</span>
                      {aiModel && <small>{aiModel}</small>}
                    </div>
                    <div className="portfolio-ai-mode-grid">
                      <button disabled={Boolean(aiBusy) || documentStatus === "ARCHIVED" || aiAvailable !== true} onClick={() => void runAiWriting("POLISH")}>
                        <strong>전체 다듬기</strong><span>자연스럽고 읽기 쉽게</span>
                      </button>
                      <button disabled={Boolean(aiBusy) || documentStatus === "ARCHIVED" || aiAvailable !== true} onClick={() => void runAiWriting("STUDENT_TONE")}>
                        <strong>학생 말투</strong><span>과장 없는 1인칭 표현</span>
                      </button>
                      <button disabled={Boolean(aiBusy) || documentStatus === "ARCHIVED" || aiAvailable !== true} onClick={() => void runAiWriting("INTERVIEW_TONE")}>
                        <strong>면접용</strong><span>말하기 쉬운 답변 구조</span>
                      </button>
                      <button disabled={Boolean(aiBusy) || documentStatus === "ARCHIVED" || aiAvailable !== true} onClick={() => void runAiWriting("ADMISSION_TONE")}>
                        <strong>진학·학과용</strong><span>관심·탐색·배움 연결</span>
                      </button>
                    </div>
                    {aiBusy && <div className="portfolio-ai-working"><i /><span>AI가 선택 기록을 확인하며 작성 중입니다…</span></div>}
                    <small>AI는 보조 도구입니다. 생성 문장은 자동 저장되지 않으며 학생이 확인 후 직접 저장합니다.</small>
                  </section>

                  <section className="portfolio-readiness-card">
                    <div className="portfolio-readiness-head">
                      <div><p className="eyebrow">READINESS</p><strong>제출 준비도</strong></div>
                      <span>{readiness.score}<small>/100</small></span>
                    </div>
                    <div className="portfolio-readiness-bar"><i style={{ width: readiness.score + "%" }} /></div>
                    <div className="portfolio-readiness-list">
                      {readiness.checks.map((item) => (
                        <div className={item.done ? "done" : ""} key={item.label}>
                          <span>{item.done ? "✓" : "○"}</span>
                          <p><strong>{item.label}</strong><small>{item.done ? "준비됨" : item.help}</small></p>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="portfolio-template-tray">
                    <p className="eyebrow">SECTION TOOLS</p>
                    <strong>빠른 섹션 추가</strong>
                    <div>
                      {SECTION_TEMPLATES.map((template) => (
                        <button type="button" key={template.key} disabled={documentStatus === "ARCHIVED"} onClick={() => insertSectionTemplate(template.key)}>
                          + {template.label}
                        </button>
                      ))}
                    </div>
                  </section>

                  <p className="eyebrow">SELECTED SOURCES</p>
                  <strong>{selectedSources.length}개 기록 선택</strong>
                  {selectedSources.length ? selectedSources.map((source) => (
                    <div className="portfolio-selected-source" key={source.id}>
                      <span>{sourceTypeLabel(source.sourceType)}</span>
                      <strong>{source.title}</strong>
                      <small>{source.trustLabel}</small>
                    </div>
                  )) : <p className="muted">선택된 원본 기록이 없습니다.</p>}
                  <p className="portfolio-trust-note">학생이 작성하거나 수정한 문장은 원본의 확인 상태와 별도로 표시됩니다.</p>
                  {documentId && documentStatus === "DRAFT" && !sharePreview && (
                    <button className="secondary full-width" disabled={dirty} onClick={() => setSharePreview(true)}>공유 전 미리보기</button>
                  )}
                </aside>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function sourceTypeLabel(type: SourceType) {
  return SOURCE_TYPES.find((item) => item.key === type)?.label || type;
}

function PublicDocumentPreview({
  profile,
  title,
  type,
  sections,
  sources,
  includeStamps,
  includeExperiences,
  purpose,
  target,
  headline
}: {
  profile: LearnerProfile;
  title: string;
  type: DocumentType;
  sections: Section[];
  sources: PortfolioSource[];
  includeStamps: boolean;
  includeExperiences: boolean;
  purpose: PortfolioPurpose;
  target: string;
  headline: string;
}) {
  const stamps = includeStamps ? sourcesOfType(sources, "LIFE_STAMP") : [];
  const experiences = includeExperiences ? sourcesOfType(sources, "EXPERIENCE") : [];
  return (
    <div className="portfolio-public-document-preview">
      <div className="portfolio-public-brand">RS LIFE PASSPORT</div>
      <p>{profile.displayName} · {typeLabel(type)}</p>
      <span className="portfolio-public-purpose">{purposeLabel(purpose)}{target ? " · " + target : ""}</span>
      <h3>{title}</h3>
      {headline && <blockquote className="portfolio-public-headline">{headline}</blockquote>}
      <div className="portfolio-public-sections">
        {sections.filter((section) => section.visible).map((section) => (
          <section key={section.key}><h4>{section.title}</h4><p>{section.body}</p></section>
        ))}
      </div>
      {experiences.length > 0 && (
        <section className="portfolio-public-sources"><h4>선택한 경험</h4>{experiences.map((item) => <article key={item.id}><strong>{item.title}</strong><span>{item.trustLabel} · {formatDate(item.occurredAt)}</span></article>)}</section>
      )}
      {stamps.length > 0 && (
        <section className="portfolio-public-sources"><h4>Life Stamp</h4>{stamps.map((item) => <article key={item.id}><strong>{item.title}</strong><span>{item.description} · {item.trustLabel}</span></article>)}</section>
      )}
    </div>
  );
}
